import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UsersThree, Money, CreditCard, Check } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCardPayment } from '../lib/cardTerminal';

const round5 = (v) => Math.round(v * 20) / 20;

// Split all court fees of one customer's bookings on a day across N players (5-Rappen shares).
export const GroupSplitDialog = ({ open, onClose, tabId, API, L, onPaid }) => {
  const payByCard = useCardPayment();
  const [data, setData] = useState(null);
  const [players, setPlayers] = useState('');
  const [paid, setPaid] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drinks, setDrinks] = useState([]);
  const [pick, setPick] = useState({});
  const [drinksFor, setDrinksFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await API.get(`/tabs/${tabId}/group-split`);
      setData(d);
      const { data: tab } = await API.get(`/tabs/${tabId}`);
      setDrinks((tab.items || []).filter((it) => it.category !== 'court_fee' && !it.paid && it.quantity - (it.paid_quantity || 0) > 0));
      setPick({});
      setPlayers(d.state?.players ? String(d.state.players) : String(d.bookings.length * 4));
      setPaid(d.state?.paid || []);
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); onClose(); }
  }, [API, tabId, L, onClose]);

  useEffect(() => { if (open) load(); }, [open, load]);
  const saveState = (pl, pd) => API.put(`/tabs/${tabId}/group-split/state`, { players: parseInt(pl, 10) || 0, paid: pd }).catch(() => {});

  const n = Math.max(1, parseInt(players, 10) || 1);
  const totalOpen = data?.total_open || 0;
  const unpaidCount = Math.max(0, n - paid.length);
  const share = unpaidCount > 0 ? round5(totalOpen / unpaidCount) : 0;
  const shareFor = (isLastOpen) => (isLastOpen ? Math.round(totalOpen * 100) / 100 : Math.min(share, totalOpen));

  const drinksTotal = drinks.reduce((sum, it) => sum + (pick[it.id] || 0) * it.price, 0);

  const renderDrinksPanel = () => (
              <div className="w-full rounded-xl border-2 border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="group-split-drinks-panel">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-800">{L('Drinks for player', 'Getränke für Spieler')} {drinksFor + 1}</p>
                {drinks.map((it) => {
                  const openQty = it.quantity - (it.paid_quantity || 0);
                  const q = pick[it.id] || 0;
                  return (
                    <div key={it.id} className="flex items-center gap-2 text-sm" data-testid={`group-split-drink-${it.id}`}>
                      <span className="flex-1 text-slate-800">{it.name} <span className="text-xs text-amber-700 font-semibold">({openQty - q} {L('open', 'offen')})</span></span>
                      <span className="text-xs text-slate-500 w-14 text-right">{it.price.toFixed(2)}</span>
                      <button type="button" disabled={q <= 0} onClick={() => setPick((p) => ({ ...p, [it.id]: q - 1 }))} className="w-8 h-8 rounded-lg border border-slate-300 bg-white font-bold disabled:opacity-40" data-testid={`group-split-drink-minus-${it.id}`}>−</button>
                      <span className="w-6 text-center font-bold" data-testid={`group-split-drink-qty-${it.id}`}>{q}</span>
                      <button type="button" disabled={q >= openQty} onClick={() => setPick((p) => ({ ...p, [it.id]: q + 1 }))} className="w-8 h-8 rounded-lg border border-slate-300 bg-white font-bold disabled:opacity-40" data-testid={`group-split-drink-plus-${it.id}`}>+</button>
                    </div>
                  );
                })}
                <p className="text-sm text-right text-amber-900">{L('Drinks', 'Getränke')}: <b>{drinksTotal.toFixed(2)} CHF</b> · {L('Total', 'Total')}: <b>{(shareFor(unpaidCount === 1) + drinksTotal).toFixed(2)} CHF</b></p>
              </div>
  );

  const pay = async (idx, method) => {
    const isLast = unpaidCount === 1;
    const amount = shareFor(isLast);
    const extra = drinksFor === idx ? Math.round(drinksTotal * 100) / 100 : 0;
    if (!(amount > 0) && !(extra > 0)) return;
    setBusy(true);
    let booked = 0;
    try {
      if (method === 'card') {
        const term = await payByCard(amount + extra, `Gruppe Spieler ${idx + 1}`);
        if (!term.ok) { toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason}`); return; }
      }
      let rest = amount;
      for (const b of data.bookings) {
        if (rest <= 0.001) break;
        if (!(b.open_amount > 0) || !b.court_fee_item_id) continue;
        const piece = Math.round(Math.min(rest, b.open_amount) * 100) / 100;
        await API.put(`/tabs/${b.tab_id}/checkout`, {
          items: [{ item_id: b.court_fee_item_id, custom_amount: piece }],
          cash_amount: method === 'cash' ? piece : 0,
          card_amount: method === 'card' ? piece : 0,
        });
        rest = Math.round((rest - piece) * 100) / 100;
        booked = Math.round((booked + piece) * 100) / 100;
      }
      if (extra > 0) {
        const items = drinks.filter((it) => pick[it.id] > 0).map((it) => ({ item_id: it.id, quantity: pick[it.id] }));
        await API.put(`/tabs/${tabId}/checkout`, {
          items, cash_amount: method === 'cash' ? extra : 0, card_amount: method === 'card' ? extra : 0,
        });
        booked = Math.round((booked + extra) * 100) / 100;
      }
      const newPaid = [...paid, idx];
      setPaid(newPaid);
      await saveState(players, newPaid);
      toast.success(`${L('Player', 'Spieler')} ${idx + 1}: ${(amount + extra).toFixed(2)} CHF${extra > 0 ? ` (${L('incl. drinks', 'inkl. Getränke')} ${extra.toFixed(2)})` : ''} ${method === 'cash' ? L('cash', 'bar') : L('card', 'Karte')} — ${L('add drinks now if needed', 'jetzt ggf. Getränke erfassen')}`);
      setDrinksFor(null);
      onPaid?.();
      onClose();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(`${e.response?.data?.detail || L('Payment failed', 'Zahlung fehlgeschlagen')}${booked > 0 ? ` — ${L('already booked', 'bereits gebucht')}: ${booked.toFixed(2)} CHF` : ''}`, { duration: 10000 });
      await load();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-split-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UsersThree size={20} /> {L('Split court fee across players', 'Platzmiete auf Spieler aufteilen')}</DialogTitle>
          <DialogDescription>{data ? `${data.customer_name} · ${data.date}` : '…'}</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm" data-testid="group-split-bookings">
              {data.bookings.map((b) => (
                <div key={b.booking_id} className="flex justify-between px-3 py-2">
                  <span className="text-slate-700">{b.court_name} · {b.start_time}–{b.end_time}</span>
                  <span className={`font-semibold ${b.open_amount > 0 ? 'text-slate-900' : 'text-emerald-600'}`}>
                    {b.open_amount > 0 ? `${b.open_amount.toFixed(2)} ${L('open', 'offen')}` : L('paid', 'bezahlt')}
                  </span>
                </div>
              ))}
              <div className="flex justify-between px-3 py-2 bg-slate-50 font-bold">
                <span>{L('Total open', 'Total offen')}</span>
                <span data-testid="group-split-total">{totalOpen.toFixed(2)} CHF</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-slate-700 shrink-0">{L('Number of players', 'Anzahl Spieler')}</label>
              <Input type="number" min="1" value={players} onChange={(e) => setPlayers(e.target.value)} onBlur={() => saveState(players, paid)} disabled={paid.length > 0}
                className="w-24 text-lg font-bold" data-testid="group-split-players" />
              {unpaidCount > 0 && totalOpen > 0 && (
                <span className="text-sm text-slate-600" data-testid="group-split-share">≈ <b>{share.toFixed(2)} CHF</b> {L('each', 'p.P.')}</span>
              )}
            </div>
            <div className="space-y-1.5" data-testid="group-split-players-list">
              {Array.from({ length: n }).map((_, i) => {
                const isPaid = paid.includes(i);
                const isLastOpen = !isPaid && unpaidCount === 1;
                return (
                  <div key={i} className={`flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border ${isPaid ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`} data-testid={`group-split-player-${i}`}>
                    <span className="flex-1 min-w-[72px] text-sm font-semibold text-slate-800 whitespace-nowrap">{L('Player', 'Spieler')} {i + 1}</span>
                    {isPaid ? (
                      <span className="text-xs font-bold text-emerald-700 flex items-center gap-1"><Check size={14} weight="bold" /> {L('paid', 'bezahlt')}</span>
                    ) : totalOpen > 0 ? (
                      <>
                        {drinks.length > 0 && (
                          <button type="button" onClick={() => { setDrinksFor(drinksFor === i ? null : i); setPick({}); }} data-testid={`group-split-drinks-${i}`}
                            className={`px-2 py-1 rounded-lg text-[11px] font-bold border transition-colors ${drinksFor === i ? 'bg-amber-500 border-amber-500 text-white' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}>
                            + {L('Drinks', 'Getränke')}
                          </button>
                        )}
                        <span className="text-sm font-bold text-slate-900 w-14 text-right" data-testid={`group-split-amount-${i}`}>{(shareFor(isLastOpen) + (drinksFor === i ? drinksTotal : 0)).toFixed(2)}</span>
                        <div className="flex gap-2 ml-auto">
                        <Button size="sm" disabled={busy} onClick={() => pay(i, 'cash')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`group-split-cash-${i}`}>
                          <Money size={14} className="mr-1" /><span>{L('Cash', 'Bar')}</span>
                        </Button>
                        <Button size="sm" disabled={busy} onClick={() => pay(i, 'card')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`group-split-card-${i}`}>
                          <CreditCard size={14} className="mr-1" /><span>{L('Card', 'Karte')}</span>
                        </Button>
                        </div>
                      </>
                    ) : <span className="text-xs text-slate-400">—</span>}
                    {!isPaid && drinksFor === i && renderDrinksPanel()}
                  </div>
                );
              })}
            </div>
            {paid.length > 0 && (
              <button type="button" onClick={() => { setPaid([]); saveState(players, []); }} className="text-[11px] text-slate-400 hover:text-slate-600 underline" data-testid="group-split-reset">
                {L('Reset player list (payments stay booked)', 'Spielerliste zurücksetzen (Zahlungen bleiben gebucht)')}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
